import { useEffect, useState } from "react";
import { CheckCircle, XCircle, Circle } from "@phosphor-icons/react";
import api from "../lib/api";

const DEFAULT_POLICY = {
  min_length: 14, require_upper: true, require_lower: true,
  require_number: true, require_special: true, block_common: true,
};

// Module-level cache so every password-set screen shares one fetch.
let _policyCache = null;

export function usePasswordPolicy() {
  const [policy, setPolicy] = useState(_policyCache);
  useEffect(() => {
    if (_policyCache) return;
    let active = true;
    api.get("/auth/password-policy")
      .then((r) => { _policyCache = r.data; if (active) setPolicy(r.data); })
      .catch(() => { if (active) setPolicy(DEFAULT_POLICY); });
    return () => { active = false; };
  }, []);
  return policy || _policyCache || DEFAULT_POLICY;
}

export function passwordChecks(pw, policy = DEFAULT_POLICY) {
  pw = pw || "";
  const p = policy || DEFAULT_POLICY;
  const checks = [{ ok: pw.length >= p.min_length, label: `At least ${p.min_length} characters` }];
  if (p.require_upper) checks.push({ ok: /[A-Z]/.test(pw), label: "An uppercase letter" });
  if (p.require_lower) checks.push({ ok: /[a-z]/.test(pw), label: "A lowercase letter" });
  if (p.require_number) checks.push({ ok: /[0-9]/.test(pw), label: "A number" });
  if (p.require_special) checks.push({ ok: /[^A-Za-z0-9]/.test(pw), label: "A special character" });
  return checks;
}

export const passwordValid = (pw, policy = DEFAULT_POLICY) =>
  passwordChecks(pw, policy).every((c) => c.ok);

export function generatePassword(len = 18) {
  const U = "ABCDEFGHJKLMNPQRSTUVWXYZ", L = "abcdefghijkmnpqrstuvwxyz", N = "23456789", S = "!@#$%^&*?-_=+";
  const all = U + L + N + S;
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  const out = [pick(U), pick(L), pick(N), pick(S)];
  for (let i = out.length; i < len; i++) out.push(pick(all));
  for (let i = out.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [out[i], out[j]] = [out[j], out[i]]; }
  return out.join("");
}

export function PasswordChecklist({ pw, className = "" }) {
  const policy = usePasswordPolicy();
  return (
    <ul className={`space-y-1 text-xs ${className}`} data-testid="password-checklist">
      {passwordChecks(pw, policy).map((c) => (
        <li key={c.label} data-testid={`pwreq-${c.ok ? "met" : "unmet"}`}
          className={`flex items-center gap-1.5 transition-colors ${c.ok ? "text-green-700" : "text-muted-foreground"}`}>
          {c.ok ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} />}
          {c.label}
        </li>
      ))}
      {policy.block_common && (
        <li className="flex items-center gap-1.5 text-muted-foreground">
          <Circle size={10} />Not a common / easily-guessed password
        </li>
      )}
    </ul>
  );
}
